/**
 * SubClientDetailPage — detail view for a single sub-client tenant.
 *
 * Route: /admin/sub-clients/:slug
 *
 * Displays comprehensive information about a managed sub-client:
 * - Overview card: monogram, name, slug, lifecycle badge
 * - Metadata section: provisioning date, contact info, tenant tier
 * - Lifecycle management: provision/suspend/reactivate actions (placeholders)
 * - Billing info: usage summary, cost allocation (placeholder)
 * - Branding cascade settings (placeholder for cycle 3+)
 *
 * Uses mock data in this cycle; will integrate with backend API in cycle 3+.
 *
 * Assumption: slug parameter passed via Router context or prop.
 * German translations added to i18n/de.ts.
 *
 * Refs: DOPLA-600, marketplace-broker-c2 brief §2.3
 */

import type { SubClientData, SubClientLifecycle } from '../components/SubClientTile';

interface SubClientDetailPageProps {
  slug: string;
  onBack: () => void;
}

// Extended mock data for detail view
interface SubClientDetail extends SubClientData {
  provisionedAt: string;
  contactEmail: string;
  tenantTier: string;
  activeUsers: number;
}

// Mock extended data - matches the 4 entries from AdminPage
const MOCK_DETAIL_DATA: Record<string, SubClientDetail> = {
  'acme-capital': {
    slug: 'acme-capital',
    name: 'Acme Capital',
    context: 'European mid-market PE fund. Securitization pilot, Deal vertical.',
    lifecycle: 'provisioned',
    provisionedAt: '2025-11-12',
    contactEmail: 'admin@acmecapital.eu',
    tenantTier: 'Standard',
    activeUsers: 24,
  },
  'nordic-fund': {
    slug: 'nordic-fund',
    name: 'Nordic Fund Partners',
    context: 'Nordic infrastructure debt. Active on all Deal modules.',
    lifecycle: 'provisioned',
    provisionedAt: '2025-10-08',
    contactEmail: 'ops@nordicfund.no',
    tenantTier: 'Premium',
    activeUsers: 47,
  },
  'alpine-investments': {
    slug: 'alpine-investments',
    name: 'Alpine Investments',
    context: 'Swiss family office. Onboarding in progress.',
    lifecycle: 'pending',
    provisionedAt: '2026-05-20',
    contactEmail: 'contact@alpine-inv.ch',
    tenantTier: 'Standard',
    activeUsers: 0,
  },
  'legacy-trust': {
    slug: 'legacy-trust',
    name: 'Legacy Trust AG',
    context: 'Account suspended — pending compliance review.',
    lifecycle: 'suspended',
    provisionedAt: '2025-07-15',
    contactEmail: 'legal@legacy-trust.de',
    tenantTier: 'Standard',
    activeUsers: 12,
  },
};

const LIFECYCLE_BADGE: Record<SubClientLifecycle, { label: string; bg: string; fg: string }> = {
  provisioned: { label: 'Provisioned', bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  pending:     { label: 'Pending',     bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)'   },
  suspended:   { label: 'Suspended',   bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)'  },
};

function Monogram({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase()).join('');
  return (
    <div
      aria-hidden="true"
      style={{
        width: 56, height: 56,
        borderRadius: 'var(--radius-md)',
        background: 'var(--dp-navy-800)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--fg-on-navy)',
        fontFamily: 'var(--font-serif)',
        fontSize: 'var(--fs-h3)', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--sp-6)' }}>
      <h3 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-h4)',
        fontWeight: 600,
        color: 'var(--fg-1)',
        marginBottom: 'var(--sp-3)',
      }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--sp-5)',
    }}>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 'var(--sp-2) 0',
      borderBottom: '1px solid var(--border-subtle)',
    }}>
      <span style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-body-sm)',
        color: 'var(--fg-2)',
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-body-sm)',
        color: 'var(--fg-1)',
        fontWeight: 500,
      }}>
        {value}
      </span>
    </div>
  );
}

export default function SubClientDetailPage({ slug, onBack }: SubClientDetailPageProps) {
  const detail = MOCK_DETAIL_DATA[slug];

  // If slug not found, show 404-style error
  if (!detail) {
    return (
      <div className="dp-shell__content" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 'var(--sp-3)',
      }}>
        <p className="dp-eyebrow">Not found</p>
        <h2>Sub-client "{slug}" does not exist</h2>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          The requested sub-client could not be found. It may have been deleted or the slug is incorrect.
        </p>
        <button className="dp-btn dp-btn--ghost" onClick={onBack}>
          ← Back to sub-clients
        </button>
      </div>
    );
  }

  const badge = LIFECYCLE_BADGE[detail.lifecycle];

  return (
    <div className="dp-shell__content">
      {/* Header with back button */}
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <button
          className="dp-btn dp-btn--ghost"
          onClick={onBack}
          style={{ marginBottom: 'var(--sp-3)', padding: 'var(--sp-1) var(--sp-2)' }}
        >
          ← Back to sub-clients
        </button>
        <p className="dp-eyebrow">Sub-client detail</p>
      </div>

      {/* Overview card */}
      <InfoCard>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-4)' }}>
          <Monogram name={detail.name} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--sp-2)' }}>
              <div>
                <p style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-2)',
                  margin: 0,
                }}>
                  {detail.slug}
                </p>
                <h2 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h2)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  margin: 'var(--sp-1) 0',
                }}>
                  {detail.name}
                </h2>
              </div>
              <span style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-meta)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                background: badge.bg,
                color: badge.fg,
                borderRadius: 'var(--radius-sm)',
                padding: '4px 10px',
              }}>
                {badge.label}
              </span>
            </div>
            {detail.context && (
              <p style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body-sm)',
                color: 'var(--fg-3)',
                margin: 0,
              }}>
                {detail.context}
              </p>
            )}
          </div>
        </div>
      </InfoCard>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 'var(--sp-6)',
        marginTop: 'var(--sp-6)',
      }}>
        {/* Metadata section */}
        <DetailSection title="Metadata">
          <InfoCard>
            <InfoRow label="Provisioned" value={detail.provisionedAt} />
            <InfoRow label="Contact email" value={detail.contactEmail} />
            <InfoRow label="Tenant tier" value={detail.tenantTier} />
            <InfoRow label="Active users" value={detail.activeUsers} />
          </InfoCard>
        </DetailSection>

        {/* Billing section — decision-2026-07-25-remove-fabricated-trial-cost.
            This card previously showed a mock monthly cost, billing cycle, payment
            method and next-invoice date. None of it was real, and a figure on an
            operator surface gets read as one and quoted back. The section stays as
            the structural slot epic-subscription-billing will fill; until it does,
            it asserts nothing. */}
        <DetailSection title="Billing">
          <InfoCard>
            <p style={{
              fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
              color: 'var(--fg-3)', margin: 0, padding: 'var(--sp-3) 0',
            }}>
              Billing is not yet enabled — no rates, invoices or payment terms exist for this
              sub-client today, and this will change. Rates will be shown before any charge applies.
            </p>
          </InfoCard>
        </DetailSection>
      </div>

      {/* Lifecycle management section */}
      <DetailSection title="Lifecycle management">
        <InfoCard>
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-3)',
            marginBottom: 'var(--sp-4)',
          }}>
            Lifecycle actions (provision, suspend, reactivate) are placeholder controls.
            Full implementation planned for cycle 3+.
          </p>
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            {detail.lifecycle === 'suspended' && (
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => console.log('[SubClientDetail] Reactivate:', detail.slug)}
              >
                Reactivate
              </button>
            )}
            {detail.lifecycle === 'provisioned' && (
              <button
                className="dp-btn dp-btn--destructive"
                onClick={() => console.log('[SubClientDetail] Suspend:', detail.slug)}
              >
                Suspend
              </button>
            )}
            {detail.lifecycle === 'pending' && (
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => console.log('[SubClientDetail] Provision:', detail.slug)}
              >
                Complete provisioning
              </button>
            )}
            <button
              className="dp-btn dp-btn--ghost"
              onClick={() => console.log('[SubClientDetail] Edit:', detail.slug)}
            >
              Edit settings
            </button>
          </div>
        </InfoCard>
      </DetailSection>

      {/* Branding cascade placeholder */}
      <DetailSection title="Branding & customization">
        <InfoCard>
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-3)',
          }}>
            Branding cascade (logo, colors, custom domain) is planned for cycle 3+ per ADR-028.
            This section will allow operators to configure sub-client branding inheritance.
          </p>
        </InfoCard>
      </DetailSection>
    </div>
  );
}
